import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import AbsoluteDropdownContainer from 'ui-library/AbsoluteDropdownContainer';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE} from 'ui-library/Colors';
import IndeterminateCheckbox from 'ui-library/IndeterminateCheckbox';
import {ScrollableTableColumn} from 'ui-library/scrollable/ScrollableTableConstants';
import {BasicEvent} from 'util/Types';

export function getVisibleColumns(
  columns: Array<ScrollableTableColumn>,
  hiddenColumnKeys: Array<string>
): Array<ScrollableTableColumn> {
  return columns.filter((column, index) => {
    const isPrimary = index === 0;
    return isPrimary || !hiddenColumnKeys.includes(column.accessorKey);
  });
}

interface Props {
  columns: Array<ScrollableTableColumn>;
  hiddenColumnKeys: Array<string>;
  onUpdateHiddenColumns: (hiddenColumnKeys: Array<string>) => void;
}

interface State {
  isOpen: boolean;
}

interface ColumnOptionProps {
  column: ScrollableTableColumn;
  hiddenColumnKeys: Array<string>;
  onUpdateHiddenColumns: (hiddenColumnKeys: Array<string>) => void;
}

export class ColumnOption extends React.Component<ColumnOptionProps> {
  _handleToggleFilter = (): void => {
    const isColumnHidden = this.props.hiddenColumnKeys.includes(this.props.column.accessorKey);
    if (isColumnHidden) {
      this.props.onUpdateHiddenColumns(
        this.props.hiddenColumnKeys.filter((accessorKey) => this.props.column.accessorKey !== accessorKey)
      );
    } else {
      this.props.onUpdateHiddenColumns([...this.props.hiddenColumnKeys, this.props.column.accessorKey]);
    }
  };

  render(): JSX.Element {
    const isColumnHidden = this.props.hiddenColumnKeys.includes(this.props.column.accessorKey);
    return (
      <label className="columnFilterDropdown-item" key={`filter-${this.props.column.header}`}>
        <div className="columnFilterDropdown-checkbox">
          <input
            type="checkbox"
            checked={!isColumnHidden}
            onChange={this._handleToggleFilter}
            className={css(styles.checkbox)}
          />
        </div>
        <div>{this.props.column.header}</div>
      </label>
    );
  }
}

/**
 * A dropdown that shows current columns and allows the user to hide them.
 *
 * The component should take all the columns for the current listing of items, both hidden and visible.
 * The parent listing component should handle filtering the list of columns based on the hiddenColumnKeys.
 * The list of hiddenColumnKeys can be a superset of the current columns, as this will handle when a column is
 * not applicable to the current listing of items, but can still be applied when the column later re-appears.
 */
class ColumnFilterDropdown extends React.Component<Props, State> {
  state: State = {isOpen: false};

  _handleClose = (): void => {
    this.setState({isOpen: false});
  };

  _toggleOpen = (e: BasicEvent): void => {
    this.setState({isOpen: !this.state.isOpen});
  };

  _handleToggleAllFilters = (): void => {
    const currentColumnKeys = this.props.columns.map((column) => column.accessorKey);
    const areAllColumnsVisible = this.props.columns.every(
      (column) => !this.props.hiddenColumnKeys.includes(column.accessorKey)
    );
    if (areAllColumnsVisible) {
      // Hide all the current columns (in addition to the already hidden columns not in the current listing).
      this.props.onUpdateHiddenColumns([...this.props.hiddenColumnKeys, ...currentColumnKeys]);
    } else {
      // Show all the current columns.
      this.props.onUpdateHiddenColumns([
        ...this.props.hiddenColumnKeys.filter((accessorKey) => !currentColumnKeys.includes(accessorKey)),
      ]);
    }
  };

  _renderMenu(): JSX.Element {
    return (
      <div className="dropdown-menu columnFilterDropdown-menu">
        <label className="columnFilterDropdown-item">
          <div className="columnFilterDropdown-checkbox">
            <IndeterminateCheckbox
              items={this.props.columns}
              selectedMap={(column) => !this.props.hiddenColumnKeys.includes(column.accessorKey)}
              onChange={this._handleToggleAllFilters}
            />
          </div>
          <div>Show all columns</div>
        </label>
        <div className="columnFilterDropdown-border" />
        {this.props.columns.map((column) => (
          <ColumnOption
            column={column}
            key={column.accessorKey}
            onUpdateHiddenColumns={this.props.onUpdateHiddenColumns}
            hiddenColumnKeys={this.props.hiddenColumnKeys}
          />
        ))}
      </div>
    );
  }

  render(): JSX.Element {
    return (
      <Tooltip container="body" placement={TooltipPlacement.TOP} title="Filter columns...">
        <div className="columnFilterDropdown">
          <AbsoluteDropdownContainer
            menu={this._renderMenu()}
            onRequestClose={this._handleClose}
            open={this.state.isOpen}
          >
            <Button
              buttonStyle={ButtonStyle.CIRCLE_ICON}
              className={classNames({'is-active': this.state.isOpen})}
              icon="columns"
              onClick={this._toggleOpen}
              size={ButtonSize.SMALL}
            />
          </AbsoluteDropdownContainer>
        </div>
      </Tooltip>
    );
  }
}

const styles = StyleSheet.create({
  checkbox: {
    accentColor: BLUE,
  },
});

export default ColumnFilterDropdown;
